import {Result} from "neverthrow"
import {DependencyList, useCallback, useEffect, useRef, useState} from "react"
import {AsyncResult, AsyncResultRequestState} from "../types/asyncResult"
import {concat, isNil} from "lodash"
import {useQueryState} from "use-location-state"
import {useMountedState} from "react-use"

export type NextFunction = () => void
export type PrevFunction = () => void
export type SetLimit = (limit: number) => void

function isTotalLargerThenLimit(result: any) {
	const {total, limit, offset} = result?.meta?.pagination || {}
	return isNil(total) || isNil(limit) || isNil(offset) || +total > +limit + +offset
}

/** Offset of the last page that can contain items for this total (e.g. total 40, limit 25 → 25). */
function lastPageOffset(total: number, limit: number) {
	return Math.max(0, Math.floor((total - 1) / limit) * limit)
}

export function usePaging<T, E>(
	initialLimit: number,
	get: (offset: number, limit: number) => Promise<Result<T, E>>,
	fLength: (x: T) => number,
	deps: DependencyList = [],
	prefix = "",
	isPendingOnNavigation = false,
	avoidQueryState = false,
	clampEmptyPage = false // if the saved page is empty after an action, go to the nearest remaining page
): [AsyncResultRequestState<T, E>, boolean, boolean, PrevFunction, NextFunction, boolean, SetLimit, boolean] {
	const [queryStateOffset, setQueryStateOffset] = useQueryState<number>(prefix + "page[offset]", 0)
	const [stateOffset, setStateOffset] = useState<number>(0)
	const [limit, setLimit] = useState(initialLimit)
	const [result, setResult] = useState<AsyncResultRequestState<T, E>>(AsyncResult.pending())
	const [hasPrev, setHasPrev] = useState(false)
	const [hasNext, setHasNext] = useState(false)
	const [hasResults, setHasResults] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const lastCallId = useRef(0)
	const isMounted = useMountedState()

	const setOffset = (newValue: number) => (avoidQueryState ? setStateOffset(newValue) : setQueryStateOffset(newValue))
	const getOffset = () => (avoidQueryState ? stateOffset : queryStateOffset)

	useEffect(() => {
		// Keep the latest request id
		const requestId = ++lastCallId.current

		;(async () => {
			setIsLoading(true)
			const offset = getOffset()
			if (isPendingOnNavigation) {
				setResult(AsyncResult.pending())
			}

			const result = await get(offset, limit)

			// if component is not mounted or the current request is not the same as the last one return
			if (!isMounted() || requestId !== lastCallId.current) {
				return
			}

			setIsLoading(false)

			result.match(
				(value) => {
					const length = fLength(value)
					const total = Number((value as any)?.meta?.pagination?.total)
					// After a list item is removed, the saved page can be empty while earlier pages still have rows.
					// Jump to the last page that still exists (or offset 0 if there are no items left).
					if (clampEmptyPage && length === 0 && offset > 0) {
						const nextOffset = total > 0 ? lastPageOffset(total, limit) : 0
						if (nextOffset !== offset) {
							setOffset(nextOffset)
							return
						}
					}

					setResult(AsyncResult.ok(value))
					setHasPrev(offset != 0)
					setHasNext(length === limit && isTotalLargerThenLimit(value))
					setHasResults(length > 0 || offset > 0)
				},
				(err) => setResult(AsyncResult.err(err))
			)
		})()
	}, concat(deps, getOffset(), limit))

	const next = () => setOffset(getOffset() + limit)
	const prev = () => setOffset(getOffset() - limit < 0 ? 0 : getOffset() - limit)

	const setLimitAndResetOffset = useCallback((newLimit: number) => {
		setOffset(0)
		setLimit(newLimit)
	}, [])

	return [result, hasPrev, hasNext, prev, next, hasResults, setLimitAndResetOffset, isLoading]
}
